// ─────────────────────────────────────────────────────────────────────────────
// «قالب‌ها» — the Settings section where a doctor builds the two kinds of
// reusable visit template, desktop surface.
//
// The screen answers, in order, the three questions a doctor arriving here has:
// what a template IS, what the two kinds are for, and what happens when one is
// applied to a visit. That explanation is the first card, not a tooltip,
// because a feature nobody understands is a feature nobody uses — and the one
// thing that must be understood here is that applying COPIES: the visit gets
// its own editable items, and nothing that happens to either side afterwards
// reaches the other.
//
// Layout follows the rest of Settings (§2.2): a P1 stack of SectionCards, one
// per kind, each a compact table of templates rather than a grid of cards.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useMemo, useState } from 'react';
import { C, soft } from '@parsina/framework/tokens';
import {
  Button, TextInput, Textarea, Select, NumberInput, EmptyState, SkeletonList,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@parsina/framework';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import { useOptionalToast } from '@parsina/framework/clinical/src/lib/useOptionalToast';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useVisitTemplates } from '@parsina/framework/clinical/src/hooks/useVisitTemplates';
import { ORDER_TYPE_KEYS } from '@parsina/framework/clinical/src/constants/patientRecord';
import {
  ORDER_TYPES, ORDER_URGENCIES, MAX_TEMPLATE_ITEMS,
  draftFromTemplate, emptyItem, moveTemplateItem, medicationItemFromDrug, draftDrugIds,
  validateTemplateDraft, hasTemplateErrors, templateItemCount,
} from '@parsina/framework/clinical/src/lib/visitTemplates';
import { RX_ROUTES, DURATIONS, RX_INSTRUCTION_PRESETS, drugNameLines } from '@parsina/framework/clinical/src/screens/Visit/Visit.shared';
import { drugFrequencyLabel } from '@parsina/framework/clinical/src/lib/drugVocabulary';
import { SectionCard, Icon, R_CARD, R_CONTROL, FieldGrid } from './SettingsKit';
import TemplateFormularySearch from './TemplateFormularySearch';
import TemplateOrderPicker from './TemplateOrderPicker';
import TemplateOrderComposer, { OrderCodeChip, OrderTypeTile } from './TemplateOrderComposer';

const URGENCY_KEYS = {
  routine: 'common.urgency.routine',
  urgent:  'common.urgency.urgent',
  stat:    'common.urgency.stat',
};

// The kit's inputs hand back the DOM event, and useSettingsForm unwraps it the
// same way. Kept as one helper so an item field cannot be wired up with the
// event object as its value.
const valueOf = (e) => (e?.target ? e.target.value : e);

// ── The explanation card ─────────────────────────────────────────────────────

const IntroCard = () => {
  const { t } = useTranslation();
  const line = (text, key) => (
    <li key={key} style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.9 }}>{text}</li>
  );
  return (
    <div
      data-testid="settings-templates-intro"
      style={{
        background: soft(C.navy, 0.05), border: `1px solid ${soft(C.navy, 0.16)}`,
        borderRadius: R_CARD, padding: '16px 20px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ color: C.navy, display: 'flex' }}><Icon.sparkles s={17} /></span>
        <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 800, color: C.textPrimary }}>
          {t('settings.templates.intro.title')}
        </h3>
      </div>
      <p style={{ margin: '0 0 8px', fontSize: 12.5, color: C.textSecondary, lineHeight: 1.9 }}>
        {t('settings.templates.intro.body')}
      </p>
      <ul style={{ margin: 0, paddingInlineStart: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {line(t('settings.templates.intro.medication'), 'med')}
        {line(t('settings.templates.intro.order'), 'ord')}
        {line(t('settings.templates.intro.copy'), 'copy')}
      </ul>
    </div>
  );
};

// ── One row of the template list ─────────────────────────────────────────────

const TemplateRow = ({ kind, template, onEdit, onDuplicate, onDelete, busy }) => {
  const { t } = useTranslation();
  const count = templateItemCount(template);
  return (
    <div
      data-testid={`template-row-${kind}`}
      data-template-id={template.id}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '11px 12px', borderRadius: R_CONTROL,
        border: `1px solid ${C.border}`, background: C.card,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }}>{template.title}</div>
        <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 2 }}>
          {count
            ? t('settings.templates.itemCount', { count })
            : t('settings.templates.noItems')}
          {template.description ? ` · ${template.description}` : ''}
        </div>
      </div>
      <Button
        variant="subtle" size="sm" disabled={busy}
        onClick={() => onEdit(template)}
        data-testid={`template-edit-${kind}`}
      >
        {t('common.actions.edit')}
      </Button>
      <Button
        variant="ghost" size="sm" disabled={busy}
        onClick={() => onDuplicate(template)}
        data-testid={`template-duplicate-${kind}`}
      >
        {t('settings.templates.duplicate')}
      </Button>
      <Button
        variant="ghost" size="sm" disabled={busy}
        onClick={() => onDelete(template)}
        data-testid={`template-delete-${kind}`}
        aria-label={t('common.actions.delete')}
        style={{ color: C.danger }}
      >
        <Icon.trash s={15} />
      </Button>
    </div>
  );
};

// ── Item editors ─────────────────────────────────────────────────────────────
//
// One row per item, with the ordering controls beside it: the order of a
// template IS the order of the نسخه lines and of the request form, so moving an
// item is an ordinary edit, not an advanced feature hidden behind a drag.

const ItemFrame = ({ index, total, onMove, onRemove, children, testId }) => {
  const { t, formatNumber } = useTranslation();
  return (
    <div
      data-testid={testId}
      style={{
        border: `1px solid ${C.border}`, borderRadius: R_CONTROL,
        padding: '10px 12px', background: C.bg,
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}
    >
      <div style={{
        width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 6,
        background: soft(C.navy, 0.1), color: C.navy,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 800,
      }}>{formatNumber(index + 1)}</div>

      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
        <Button
          variant="ghost" size="sm" disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          aria-label={t('settings.templates.moveUp')}
          data-testid="template-item-up"
        >↑</Button>
        <Button
          variant="ghost" size="sm" disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
          aria-label={t('settings.templates.moveDown')}
          data-testid="template-item-down"
        >↓</Button>
        <Button
          variant="ghost" size="sm"
          onClick={() => onRemove(index)}
          aria-label={t('settings.templates.removeItem')}
          data-testid="template-item-remove"
          style={{ color: C.danger }}
        ><Icon.trash s={14} /></Button>
      </div>
    </div>
  );
};

// A dosing field is a free-text input WITH suggestions, never a closed list —
// the same rule the visit's نسخه row follows (a paediatric mg/kg dose is not in
// `commonDosages`). The suggestions come from the picked formulary row where it
// has them, and from the visit's own route / duration / instruction lists
// otherwise, so a template can only ever say what the visit tab would offer.
const SuggestInput = ({ id, label, value, options, onChange, labelFor, testId }) => {
  const listId = options?.length ? `${id}-list` : undefined;
  return (
    <>
      <TextInput
        label={label}
        value={value}
        onChange={onChange}
        list={listId}
        data-testid={testId}
      />
      {listId && (
        <datalist id={listId}>
          {options.map((o) => {
            const shown = labelFor ? labelFor(o) : o;
            return <option key={o} value={o} {...(shown !== o ? { label: shown } : {})} />;
          })}
        </datalist>
      )}
    </>
  );
};

const MedicationItemFields = ({ item, errors, onChange, index = 0 }) => {
  const { t } = useTranslation();
  const field = (key) => (e) => onChange(key, valueOf(e));
  const id = `tpl-med-${index}`;
  const fromFormulary = Boolean(item.drugId);
  const names = drugNameLines(item);
  return (
    <>
      {fromFormulary ? (
        // A formulary drug keeps its name: editing it would silently break the
        // `drugId` join. A wrong pick is removed and picked again.
        <div
          data-testid="template-item-locked-name"
          style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}
        >
          {/* Latin first, so in this RTL panel it sits on the RIGHT and the
              Persian product string follows on its left — the same order as the
              نسخه picker and the printed نسخه. See `drugNameLines`. */}
          <span dir="auto" style={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }}>{names.primary}</span>
          {names.secondary && (
            <span dir="auto" style={{ fontSize: 11, color: C.textMuted }}>
              {names.secondary}
            </span>
          )}
          <span style={{
            fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 20,
            background: soft(C.teal, 0.14), color: C.teal,
          }}>
            {t('settings.templates.formularyTag')}
          </span>
        </div>
      ) : (
        <FieldGrid max={2} gap={10}>
          <TextInput
            label={t('settings.templates.drug.name')} required
            value={item.name} onChange={field('name')}
            error={errors?.name && t(errors.name)}
            data-testid="template-item-name"
          />
          <TextInput
            label={t('settings.templates.drug.generic')}
            value={item.genericName} onChange={field('genericName')}
          />
        </FieldGrid>
      )}
      <FieldGrid max={2} gap={10}>
        <SuggestInput
          id={`${id}-dosage`} label={t('settings.templates.drug.dosage')}
          value={item.dosage} options={item.commonDosages || []}
          onChange={field('dosage')} testId="template-item-dosage"
        />
        <SuggestInput
          id={`${id}-frequency`} label={t('settings.templates.drug.frequency')}
          value={item.frequency} options={item.commonFrequencies || []}
          labelFor={(v) => drugFrequencyLabel(v, t)}
          onChange={field('frequency')} testId="template-item-frequency"
        />
        <SuggestInput
          id={`${id}-route`} label={t('settings.templates.drug.route')}
          value={item.route} options={RX_ROUTES(t)}
          onChange={field('route')} testId="template-item-route"
        />
        <SuggestInput
          id={`${id}-duration`} label={t('settings.templates.drug.duration')}
          value={item.duration} options={DURATIONS(t)}
          onChange={field('duration')} testId="template-item-duration"
        />
        <TextInput
          label={t('settings.templates.drug.quantity')}
          value={item.quantity} onChange={field('quantity')}
          data-testid="template-item-quantity"
        />
      </FieldGrid>
      <div style={{ marginTop: 10 }}>
        <SuggestInput
          id={`${id}-instructions`} label={t('settings.templates.drug.instructions')}
          value={item.instructions} options={RX_INSTRUCTION_PRESETS(t)}
          onChange={field('instructions')} testId="template-item-instructions"
        />
      </div>
    </>
  );
};

/**
 * One line of the برگه درخواست.
 *
 * Reads as a list entry, not a form: type, name, identity — and everything a
 * doctor leaves at its default (urgency routine, 0 sessions, no note) stays
 * folded away behind «جزئیات». The four fields that were always open were
 * almost always untouched, and they buried the one thing worth reading.
 */
const OrderItemFields = ({ item, errors, onChange, onPatch }) => {
  const { t, formatNumber } = useTranslation();
  const [open, setOpen] = useState(false);
  const field = (key) => (e) => onChange(key, valueOf(e));
  const linked = !!item.catalogId;
  const urgencyOptions = useMemo(
    () => ORDER_URGENCIES.map((value) => ({ value, label: t(URGENCY_KEYS[value]) })),
    [t],
  );
  const typeOptions = useMemo(
    () => ORDER_TYPES.map((value) => ({ value, label: t(ORDER_TYPE_KEYS[value]) })),
    [t],
  );

  // Extras a doctor HAS set are summarised on the closed line: folding a value
  // away is only honest if its presence is still visible.
  const extras = [
    item.urgency && item.urgency !== 'routine' ? t(URGENCY_KEYS[item.urgency]) : null,
    item.sessionCount > 0 ? t('settings.templates.orderSessionsShort', { count: formatNumber(item.sessionCount) }) : null,
    item.notes ? t('settings.templates.order_.notes') : null,
  ].filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {linked ? (
          <OrderTypeTile orderType={item.orderType} fromCatalogue />
        ) : (
          <div style={{ width: 118, flexShrink: 0 }}>
            <Select
              value={item.orderType} onChange={field('orderType')}
              options={typeOptions}
              aria-label={t('settings.templates.order_.type')}
              data-testid="template-item-order-type"
            />
          </div>
        )}

        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          {linked ? (
            <div
              dir="auto"
              data-testid="template-item-name"
              style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >{item.name}</div>
          ) : (
            // A hand-written line has nothing but its name, so the name stays
            // editable in place — and the same box searches the catalogue, which
            // is how a typed line becomes an identified one.
            <TemplateOrderPicker
              value={item.name}
              onChange={field('name')}
              onPick={(choice) => onPatch({
                name: choice.name,
                code: choice.code || '',
                orderType: choice.orderType || 'lab',
                catalogId: choice.catalogId || null,
              })}
              orderType={item.orderType}
              catalogId={item.catalogId}
              error={errors?.name && t(errors.name)}
              bare
            />
          )}
        </div>

        <OrderCodeChip item={item} />

        {extras.map((label) => (
          <span key={label} style={{
            fontSize: 10.5, color: C.textSecondary, background: soft(C.navy, 0.05),
            padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap',
          }}>{label}</span>
        ))}

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="template-item-details"
          style={{
            border: 'none', background: 'transparent', cursor: 'pointer', flexShrink: 0,
            color: C.textSecondary, fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700,
            padding: '2px 4px',
          }}
        >
          {t(open ? 'common.actions.close' : 'settings.templates.orderDetails')} {open ? '▴' : '▾'}
        </button>
      </div>

      {linked && errors?.name && (
        <div style={{ fontSize: 11.5, color: C.red }}>{t(errors.name)}</div>
      )}

      {open && (
        <div
          data-testid="template-item-details-panel"
          style={{
            borderTop: `1px dashed ${C.border}`, paddingTop: 10,
            display: 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          <FieldGrid min={170} max={2} gap={10}>
            <Select
              label={t('settings.templates.order_.urgency')}
              value={item.urgency} onChange={field('urgency')}
              options={urgencyOptions}
              placeholder={t('common.actions.select')}
              data-testid="template-item-urgency"
            />
            <NumberInput
              label={t('settings.templates.order_.sessions')}
              value={item.sessionCount}
              onChange={(e) => onChange('sessionCount', Number(valueOf(e)) || 0)}
              min={0} max={100}
              error={errors?.sessionCount && t(errors.sessionCount)}
            />
          </FieldGrid>

          <TextInput
            label={t('settings.templates.order_.notes')}
            value={item.notes} onChange={field('notes')}
          />

          {linked ? (
            // Unlinking is the only way to correct a wrong pick, and it is
            // explicit: the code goes with the service it belonged to, rather
            // than staying behind to describe a different one.
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11.5, color: C.textMuted }}>{t('settings.templates.orderCatalogLinked')}</span>
              <Button
                variant="ghost" size="sm"
                onClick={() => onPatch({ catalogId: null, code: '' })}
                data-testid="template-item-unlink"
              >{t('settings.templates.orderChangeService')}</Button>
            </div>
          ) : (
            <TextInput
              label={t('settings.templates.order_.code')}
              helperText={t('settings.templates.orderManualCodeHint')}
              value={item.code} onChange={field('code')}
              data-testid="template-item-code"
            />
          )}
        </div>
      )}
    </div>
  );
};

// ── The editor ───────────────────────────────────────────────────────────────

const TemplateEditor = ({ kind, open, template, onClose, onSave }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => draftFromTemplate(kind, template));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Errors are computed on every render but only SHOWN once the doctor has
  // tried to save: an empty title is not a mistake while the form is still
  // being filled in.
  const [submitted, setSubmitted] = useState(false);

  // A fresh open is a fresh draft. Keyed remount would do it too, but this keeps
  // the reset next to the state it resets.
  const [openedFor, setOpenedFor] = useState(template?.id ?? null);
  if (open && openedFor !== (template?.id ?? null)) {
    setOpenedFor(template?.id ?? null);
    setDraft(draftFromTemplate(kind, template));
    setSubmitted(false);
    setSaveError(null);
  }

  const errors = useMemo(() => validateTemplateDraft(kind, draft), [kind, draft]);
  const invalid = hasTemplateErrors(errors);

  const setField = (key) => (e) => setDraft((d) => ({ ...d, [key]: valueOf(e) }));

  const setItemField = (index) => (key, value) => setDraft((d) => ({
    ...d,
    items: d.items.map((item, i) => (i === index ? { ...item, [key]: value } : item)),
  }));

  // Several fields change together on a catalogue pick, and they must land in ONE
  // setState: three sequential single-field updates would each start from the
  // draft as it was when the handler was created, and only the last would stick.
  const patchItem = (index) => (patch) => setDraft((d) => ({
    ...d,
    items: d.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
  }));

  // A pick fills the name, the code and the catalogue id — and corrects the type
  // if the doctor searched across it. `code` is CLEARED rather than kept when the
  // chosen service has none (imaging): a code left behind from a previous pick
  // would claim this service is something it is not.
  // A catalogue pick appends a complete line — name, code, type and id — and
  // never the same service twice, exactly as the formulary pick does.
  const addCatalogOrder = (choice) => setDraft((d) => {
    if (d.items.length >= MAX_TEMPLATE_ITEMS) return d;
    if (choice.catalogId && d.items.some((it) => it.catalogId === choice.catalogId)) return d;
    return {
      ...d,
      items: [...d.items, {
        ...emptyItem('order'),
        name: choice.name,
        code: choice.code || '',
        orderType: choice.orderType || 'lab',
        catalogId: choice.catalogId || null,
      }],
    };
  });
  const catalogIds = useMemo(
    () => new Set(draft.items.map((it) => it.catalogId).filter(Boolean)),
    [draft.items],
  );

  const addItem = () => setDraft((d) => ({ ...d, items: [...d.items, emptyItem(kind)] }));
  // A formulary pick appends a complete item — id, names, usual dose and
  // frequency — and never the same drug twice, as the visit's picker does.
  const addDrug = (drug) => setDraft((d) => {
    if (d.items.length >= MAX_TEMPLATE_ITEMS) return d;
    if (d.items.some((item) => item.drugId && item.drugId === drug.id)) return d;
    return { ...d, items: [...d.items, medicationItemFromDrug(drug)] };
  });
  const drugIds = useMemo(() => draftDrugIds(draft), [draft]);
  const removeItem = (index) => setDraft((d) => ({ ...d, items: d.items.filter((_, i) => i !== index) }));
  const moveItem = (from, to) => setDraft((d) => ({ ...d, items: moveTemplateItem(d.items, from, to) }));

  const save = async () => {
    setSubmitted(true);
    if (invalid) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(draft);
      onClose();
    } catch (err) {
      // Shown beside the form, not as a toast that outlives the modal: the
      // doctor still has the unsaved template in front of them and can retry.
      setSaveError(t('settings.templates.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const ItemFields = kind === 'order' ? OrderItemFields : MedicationItemFields;
  const atLimit = draft.items.length >= MAX_TEMPLATE_ITEMS;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid={`template-editor-${kind}`}>
        <DialogHeader>
          <DialogTitle>
            {template ? t('common.actions.edit') : t(`settings.templates.${kind}.new`)}
          </DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <TextInput
            label={t('settings.templates.titleLabel')} required
            placeholder={t('settings.templates.titlePlaceholder')}
            value={draft.title} onChange={setField('title')}
            error={submitted && errors.title ? t(errors.title) : undefined}
            data-testid="template-title"
          />
          <Textarea
            label={t('settings.templates.descriptionLabel')}
            placeholder={t('settings.templates.descriptionPlaceholder')}
            value={draft.description} onChange={setField('description')}
            rows={2}
            error={submitted && errors.description ? t(errors.description) : undefined}
          />

          {kind === 'medication' && (
            <TemplateFormularySearch excludeIds={drugIds} onPick={addDrug} disabled={atLimit} />
          )}
          {kind === 'order' && (
            <TemplateOrderComposer
              excludeIds={catalogIds}
              onPick={addCatalogOrder}
              onManual={addItem}
              disabled={atLimit}
            />
          )}

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary }}>
                {t('settings.templates.items')}
              </span>
              <div style={{ flex: 1 }} />
              {/* Orders add from the composer above; a second button here would
                  offer the same action twice, in the weaker place. */}
              {kind === 'medication' && (
                <Button
                  variant="subtle" size="sm" onClick={addItem} disabled={atLimit}
                  data-testid="template-add-item"
                >
                  + {t('settings.templates.addManual')}
                </Button>
              )}
            </div>

            {draft.items.length === 0 ? (
              <div
                data-testid="template-items-empty"
                style={{
                  border: `1px dashed ${C.border}`, borderRadius: R_CONTROL,
                  padding: '14px 16px', fontSize: 12, color: C.textMuted, lineHeight: 1.8,
                }}
              >
                {t('settings.templates.emptyItems')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {draft.items.map((item, index) => (
                  <ItemFrame
                    key={index}
                    index={index} total={draft.items.length}
                    onMove={moveItem} onRemove={removeItem}
                    testId="template-item"
                  >
                    <ItemFields
                      item={item}
                      index={index}
                      errors={submitted ? errors.items[index] : undefined}
                      onChange={setItemField(index)}
                      onPatch={patchItem(index)}
                    />
                  </ItemFrame>
                ))}
              </div>
            )}
            {submitted && errors.itemCount && (
              <div style={{ marginTop: 8, fontSize: 12, color: C.danger }}>{t(errors.itemCount)}</div>
            )}
          </div>

          {saveError && (
            <div
              role="alert" data-testid="template-save-error"
              style={{
                background: soft(C.danger, 0.1), border: `1px solid ${soft(C.danger, 0.3)}`,
                borderRadius: R_CONTROL, padding: '9px 12px', fontSize: 12, color: C.danger,
              }}
            >
              {saveError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.actions.cancel')}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="template-save">
            {saving ? t('common.state.saving') : t('common.actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── One kind's card ──────────────────────────────────────────────────────────

const TemplateKindCard = ({ kind, icon, iconBg, iconColor }) => {
  const { t } = useTranslation();
  const toast = useOptionalToast();
  const store = useVisitTemplates(kind);
  const [editing, setEditing] = useState(null);   // { template } | { template: null } | null
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const onSave = useCallback(async (draft) => {
    const target = editing?.template;
    if (target) await store.update(target.id, draft);
    else await store.create(draft);
    toast.success(t('settings.templates.saved'));
  }, [editing, store, toast, t]);

  const onDuplicate = useCallback(async (template) => {
    setBusy(true);
    try {
      await store.duplicate(template.id);
      toast.success(t('settings.templates.duplicated'));
    } catch (err) {
      toast.error(t('settings.templates.duplicateFailed'));
    } finally { setBusy(false); }
  }, [store, toast, t]);

  const onConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await store.remove(pendingDelete.id);
      toast.success(t('settings.templates.deleted'));
    } catch (err) {
      toast.error(t('settings.templates.deleteFailed'));
    } finally {
      setBusy(false);
      setPendingDelete(null);
    }
  }, [pendingDelete, store, toast, t]);

  return (
    <>
      <SectionCard
        testId={`settings-templates-${kind}`}
        icon={icon} iconBg={iconBg} iconColor={iconColor}
        title={t(`settings.templates.${kind}.label`)}
        subtitle={t(`settings.templates.${kind}.explain`)}
        actions={(
          <Button
            variant="subtle" size="sm"
            onClick={() => setEditing({ template: null })}
            data-testid={`template-new-${kind}`}
          >
            + {t(`settings.templates.${kind}.new`)}
          </Button>
        )}
      >
        {store.loading && !store.loaded ? (
          <SkeletonList count={2} avatar={false} trailing={48} label={t('common.state.loading')} />
        ) : store.error && !store.loaded ? (
          <ErrorState
            title={t('settings.templates.loadFailed')}
            error={store.error}
            onRetry={store.reload}
          />
        ) : store.templates.length === 0 ? (
          <EmptyState
            title={t(`settings.templates.${kind}.empty`)}
            description={t(`settings.templates.${kind}.explain`)}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {store.templates.map((template) => (
              <TemplateRow
                key={template.id}
                kind={kind} template={template} busy={busy}
                onEdit={(row) => setEditing({ template: row })}
                onDuplicate={onDuplicate}
                onDelete={setPendingDelete}
              />
            ))}
          </div>
        )}
      </SectionCard>

      {editing && (
        <TemplateEditor
          kind={kind}
          open
          template={editing.template}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={t('settings.templates.deleteConfirmTitle')}
        // Says out loud what deleting does NOT do — the question a doctor
        // hesitating over this button is actually asking.
        message={t('settings.templates.deleteConfirmBody', { title: pendingDelete?.title || '' })}
        confirmLabel={t('common.actions.delete')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={onConfirmDelete}
        onOpenChange={(next) => { if (!next) setPendingDelete(null); }}
      />
    </>
  );
};

export default function TemplatesPanel() {
  return (
    <div
      data-testid="settings-templates"
      style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
    >
      <IntroCard />
      <TemplateKindCard
        kind="medication"
        icon={<Icon.badge s={20} />}
        iconBg={soft(C.teal, 0.12)}
        iconColor={C.teal}
      />
      <TemplateKindCard
        kind="order"
        icon={<Icon.clinic s={20} />}
        iconBg={soft(C.navy, 0.09)}
        iconColor={C.navy}
      />
    </div>
  );
}

export { TemplateEditor, TemplateKindCard };
